import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions, EventClickArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import esLocale from '@fullcalendar/core/locales/es';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { HeaderSophiaComponent } from '../../common/components/header-sophia/header-sophia.component';
import { ModalDetalleConsultaComponent } from '../../common/components/detalle-consulta/modal-detalle-consulta.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ModalBloqueoHorarioComponent } from '../../common/components/bloqueo-horario/modal-bloqueo-horario.component';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCardModule } from '@angular/material/card';
import {
  DataAgendamiento,
  IDataEvent,
  TipoEvento,
} from '@shared/interfaces/tratamiento/evento-agendamiento.interface';
import { Router } from '@angular/router';
import {
  IDataDetalleNuevaConsulta,
  IRespDetalleNuevaConsulta,
} from '@shared/interfaces/tratamiento/detalle-nueva-consulta.interface';
import { CommonModule } from '@angular/common';
import { ModalExitoBloqueoHorarioComponent } from '../../../shared/components/modal-exito-bloqueo-horario/modal-exito-bloqueo-horario.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import { convertirUTCaLocal } from '../../../shared/helpers/date.helper';
import { DetalleDesbloqueoHorarioComponent } from '../../common/components/detalle-desbloqueo-horario/detalle-desbloqueo-horario.component';
import {
  InfoDetalleModalNuevaConsultaService,
  ObtenerAgendamientosMedicosService,
  ServiceFormAnamnesisService,
  ServiceFormIdentificacionService,
  ShareDataService,
  StorageService,
  UserService,
} from '@shared/services/index';
import { HasAccessDirective } from '../../../shared/directives/has-access/has-access.directive';
import { AppPermissions, StorageKeys, StorageType, ValidRoles } from '../../../shared/enums';
import { CentroMedico, Medicos } from '@shared/interfaces/perfil.interface';
import { AuthenticationService } from '../../services/authentication.service';
import { ChangeMedicalCenterComponent } from '../medical-centers/components/change-medical-center/change-medical-center.component';
import { ModalErrorGeneralComponent } from '../../common/modal-error-general/modal-error-general.component';

@Component({
  selector: 'soph-calendario-agendamiento',
  standalone: true,
  imports: [
    FullCalendarModule,
    MatDialogModule,
    MatInputModule,
    HeaderSophiaComponent,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatDatepickerModule,
    MatNativeDateModule,
    CommonModule,
    MatTooltipModule,
    HasAccessDirective,
    ChangeMedicalCenterComponent,
  ],
  templateUrl: './calendario-agendamiento.component.html',
  styleUrl: './calendario-agendamiento.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarioAgendamientoComponent implements OnInit, OnDestroy {
  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;
  calendarOptions: CalendarOptions = {
    initialDate: new Date().toISOString(),
    initialView: 'timeGridWeek',
    timeZone: 'local',
    locale: esLocale,
    firstDay: 1,
    plugins: [timeGridPlugin, dayGridPlugin, interactionPlugin],
    headerToolbar: {
      left: 'prev,next',
      center: 'title',
      right: 'timeGridDay,timeGridWeek,dayGridMonth',
    },
    slotLabelFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
    dayHeaderFormat: { weekday: 'long', day: '2-digit' },
    //TODO: Esto debe ser configurable
    contentHeight: 'auto',
    slotDuration: '00:30:00',
    slotLabelInterval: '00:30:00',
    slotMinTime: '07:00:00',
    slotMaxTime: '22:30:00',
    selectMirror: true,
    dayMaxEvents: false,
    eventClick: this.openModalDetalleConsulta.bind(this),
    datesSet: this.setEventos.bind(this),
    eventDidMount: (info) => {
      info.el.setAttribute('matTooltip', info.event.title);
      info.el.classList.add('mat-tooltip-trigger');
    },
    dateClick: (arg) => this.openModalNuevaConsultaDesdeCalendario(arg),
    dayHeaderContent: (args) => {
      if (args.view.type === 'dayGridMonth') {
        return args.text.split(' ')[0];
      }
      return args.text;
    },

    eventContent: (arg) => {
      const backgroundColor = arg.event.backgroundColor;
      return {
        html: `
          <div style="
          cursor: pointer;
          background-color: ${backgroundColor};
          color: #0c2447;">
            ${arg.event.title}
          </div>
        `,
      };
    },
  };

  filtro = '';
  flagFiltros: boolean = false;
  selected: Date | null = null;
  details: IRespDetalleNuevaConsulta | null = null;
  allEvents: WritableSignal<IDataEvent[]> = signal([]);
  readonly dialog = inject(MatDialog);
  filtroActivo: string | null = null;

  protected readonly AppPermissions = AppPermissions;
  private readonly userService: UserService = inject(UserService);
  private readonly auth: AuthenticationService = inject(AuthenticationService);
  private readonly storage: StorageService = inject(StorageService);
  private subscription: Subscription[] = [];

  constructor(
    private readonly _eventosAgendamientosService: ObtenerAgendamientosMedicosService,
    private readonly _detalleConsulta: InfoDetalleModalNuevaConsultaService,
    private router: Router,
    private readonly _refreshCalendarService: ShareDataService,
    private datosPersonalesService: ServiceFormIdentificacionService,
    private datosMedicosService: ServiceFormAnamnesisService
  ) {}

  async ngOnInit(): Promise<void> {
    const subscription = this._refreshCalendarService.refreshCalendar$.subscribe(() => {
      this.refreshCalendar();
    });
    this.subscription.push(subscription);
    this.datosPersonalesService.getDatosPersonales().subscribe();
    this.datosMedicosService.getDatosMedicos().subscribe();
  }

  hasAssistantRole(): boolean {
    return this.auth.hasAtLeastOneRole([ValidRoles.ASSISTANT, ValidRoles.MEDICAL_ASSISTANT]);
  }

  private getDoctorId(): string | undefined {
    return this.storage.getItem<Medicos>(StorageKeys.CURRENT_DOCTOR, StorageType.LOCAL)?.idMedico;
  }

  private getMedicalCenterId(): string | undefined {
    return this.storage.getItem<CentroMedico>(StorageKeys.CURRENT_MEDICAL_CENTER, StorageType.LOCAL)
      ?.idCentroMedico;
  }

  refreshCalendar() {
    const calendarApi = this.calendarComponent?.getApi();
    if (!calendarApi) {
      return;
    }

    const { currentStart, currentEnd } = calendarApi.view;
    const start = currentStart.toISOString().split('T')[0];
    const end = currentEnd.toISOString().split('T')[0];

    this._eventosAgendamientosService
      .getEventos(start, end, this.getDoctorId()!, this.getMedicalCenterId()!)
      .subscribe({
        next: ({ data }) => {
          calendarApi.removeAllEvents();
          if (!data?.length) return;
          this.allEvents.set(data);
          calendarApi.addEventSource(data);
        },
        error: (err) => console.error('Error al refrescar eventos', err),
      });
  }

  eventDidMount(info: any) {
    info.el.setAttribute('title', info.event.title);
  }

  setEventos(dateInfo: any): void {
    const inicioFormateada = new Date(dateInfo.start).toISOString().split('T')[0];
    const terminoFormateada = new Date(dateInfo.end).toISOString().split('T')[0];
    const calendarApi = dateInfo.view.calendar;

    this._eventosAgendamientosService
      .getEventos(
        inicioFormateada,
        terminoFormateada,
        this.getDoctorId()!,
        this.getMedicalCenterId()!
      )
      .subscribe({
        next: (res) => {
          if (res.data?.length === 0) return;

          res.data.map((e) => {
            e.start = convertirUTCaLocal(e.start);
            e.end = convertirUTCaLocal(e.end);
          });

          this.allEvents.set(res.data);

          calendarApi.removeAllEvents();

          if (this.filtroActivo) {
            const eventosFiltrados = res.data.filter((e) => {
              const tipoAtencion = (e.metadata as DataAgendamiento).tipoAtencion;
              return tipoAtencion === this.filtroActivo;
            });
            calendarApi.addEventSource(eventosFiltrados);
          } else {
            calendarApi.addEventSource(res.data);
          }
        },
        error: (err) => {
          console.error('Error al obtener eventos', err);
        },
      });
  }

  filtrarEventosPorTipo(tipoFiltro: string): void {
    const calendarApi = this.calendarComponent.getApi();

    if (tipoFiltro === 'DELETE') {
      this.filtroActivo = null;
      const eventos = this.allEvents();
      calendarApi.removeAllEvents();
      calendarApi.addEventSource(eventos);
    } else {
      this.filtroActivo = tipoFiltro;
      const eventosFiltrados = this.allEvents().filter((e) => {
        const tipoAtencion = (e.metadata as DataAgendamiento).tipoAtencion;
        return tipoAtencion === tipoFiltro;
      });
      calendarApi.removeAllEvents();
      calendarApi.addEventSource(eventosFiltrados);
    }
  }

  filterTipoDomiciliaria(): void {
    this.flagFiltros = true;
    this.filtrarEventosPorTipo('DOMICILIARIA');
  }

  filterTipoPresencial(): void {
    this.flagFiltros = true;
    this.filtrarEventosPorTipo('PRESENCIAL');
  }

  filterTipoTelemedicina(): void {
    this.flagFiltros = true;
    this.filtrarEventosPorTipo('TELEMEDICINA');
  }

  eliminarFiltrosTipo(): void {
    this.flagFiltros = false;
    this.filtrarEventosPorTipo('DELETE');
  }

  openModalDetalleConsulta(clickInfo: EventClickArg) {
    const tipo = clickInfo.event.extendedProps['tipo'];
    const idAgendamiento = clickInfo.event._def.publicId;
    const estado = clickInfo.event.extendedProps['metadata']['estadoAtencion'];
    const idPaciente = clickInfo.event.extendedProps['metadata']['idPaciente'];
    if (!idAgendamiento) return;

    if (tipo === TipoEvento.Agendamiento) {
      this._detalleConsulta.getDetalleConsulta(idAgendamiento).subscribe({
        next: (res: IDataDetalleNuevaConsulta) => {
          if (res.success) {
            this.details = res.data;
            this.dialog.open(ModalDetalleConsultaComponent, {
              width: '454px',
              data: {
                fechaAtencion: this.details.fechaAtencion,
                horaAtencion: this.details.horaAtencion,
                tipoATencion: this.details.tipoATencion,
                nombrePaciente: this.details.nombrePaciente,
                telefonoPaciente: this.details.telefonoPaciente,
                detalleAtencion: this.details.detalleAtencion,
                idAgendamiento: this.details.idAgendamiento,
                estado,
                idPaciente,
              },
            });
          }
        },
        error: (error) => {
          this.refreshCalendar();

          this.dialog.open(ModalErrorGeneralComponent, {
            width: '452px',
            disableClose: true,
            data: {
              message: error.error.error.message,
              callbackUrl: '/calendario',
            },
          });
        },
      });
    } else {
      this.dialog.open(DetalleDesbloqueoHorarioComponent, {
        data: {
          fechaAtencion: clickInfo.event._instance?.range.start,
          idAgendamiento: clickInfo.event._def.publicId,
          fechaTermino: clickInfo.event._instance?.range.end,
          evento: this.allEvents().filter((e) => e.id === idAgendamiento)[0],
        },
      });
    }
  }

  openModalNuevaConsulta(): void {
    this.router.navigate(['/ingreso']);
  }

  openModalNuevaConsultaDesdeCalendario(arg: DateClickArg): void {
    this.router.navigate(['/ingreso'], { queryParams: { fecha: arg.dateStr } });
  }

  public openBlockModal() {
    const dialogRef = this.dialog.open(ModalBloqueoHorarioComponent, {});
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
      }
    });
  }

  openBloqueoHorarios(): void {
    this.router.navigate(['/bloqueo-horario']);
  }

  openModalPrueba() {
    this.dialog.open(ModalExitoBloqueoHorarioComponent);
  }

  ngOnDestroy(): void {
    this.subscription.forEach((sub) => sub.unsubscribe());
    this.subscription = [];
  }
}
